import { Injectable, signal } from "@angular/core";

declare global {
  interface Window {
    Tawk_API?: any;
    Tawk_LoadStart?: Date;
  }
}

@Injectable({ providedIn: "root" })
export class TawkService {
  private loaded = false;
  private loadingPromise: Promise<void> | null = null;
  private eventsWired = false;

  readonly lastMessage = signal<string>("");

  load(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = new Promise<void>((resolve) => {
      // If the script tag already exists (e.g. hot reload), don't add another.
      const existing = document.querySelector<HTMLScriptElement>('script[src*="embed.tawk.to/69de48523d91a21c36a8ba56"]');
      if (existing) {
        this.loaded = true;
        this.wireEvents();
        queueMicrotask(() => this.hide());
        resolve();
        return;
      }

      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      // Keep the floating launcher hidden; chat opens only via maximize() (e.g. match tap).
      const prevOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = () => {
        try {
          prevOnLoad?.();
        } catch {
          // ignore
        }
        try {
          window.Tawk_API?.hideWidget?.();
        } catch {
          // ignore
        }
      };

      const s1 = document.createElement("script");
      s1.async = true;
      s1.src = "https://embed.tawk.to/69de48523d91a21c36a8ba56/1jm64l0ff";
      s1.charset = "UTF-8";
      s1.setAttribute("crossorigin", "*");
      s1.onload = () => {
        this.loaded = true;
        this.wireEvents();
        queueMicrotask(() => this.hide());
        resolve();
      };
      s1.onerror = () => resolve(); // don't block the app if it fails

      document.head.appendChild(s1);
    });

    return this.loadingPromise;
  }

  private wireEvents(): void {
    if (this.eventsWired) return;
    this.eventsWired = true;

    try {
      // Capture the most recent message (visitor or agent) for display.
      window.Tawk_API = window.Tawk_API || {};

      const setFromAny = (payload: any) => {
        const text =
          (typeof payload === "string" ? payload : payload?.message ?? payload?.msg ?? payload?.text ?? payload?.body ?? "")
            ?.toString?.()
            ?.trim?.() ?? "";
        if (text) this.lastMessage.set(text);
      };

      // Tawk commonly exposes these callbacks.
      window.Tawk_API.onChatMessageVisitor = (message: any) => setFromAny(message);
      window.Tawk_API.onChatMessageAgent = (message: any) => setFromAny(message);

      // Some builds expose a generic message callback.
      if (!window.Tawk_API.onChatMessage) {
        window.Tawk_API.onChatMessage = (message: any) => setFromAny(message);
      }

      // When the visitor closes or minimizes chat, hide the launcher again (no bubble).
      const rehide = () => {
        try {
          window.Tawk_API?.hideWidget?.();
        } catch {
          // ignore
        }
      };
      window.Tawk_API.onChatMinimized = rehide;
      window.Tawk_API.onChatHidden = rehide;
    } catch {
      // ignore
    }
  }

  show(): void {
    try {
      window.Tawk_API?.showWidget?.();
    } catch {
      // ignore
    }
  }

  /** Opens the chat panel. Works even when the floating widget is hidden. */
  openChat(): void {
    try {
      // Some Tawk builds won't maximize unless the widget is shown first.
      window.Tawk_API?.showWidget?.();
      window.Tawk_API?.maximize?.();
    } catch {
      // ignore
    }
  }

  maximize(): void {
    this.openChat();
  }

  hide(): void {
    try {
      window.Tawk_API?.hideWidget?.();
    } catch {
      // ignore
    }
  }
}

